import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api';
import { playTick } from '../lib/tick';
import { sqrtBuySubtotal, sqrtSellSubtotal } from '../lib/curve';

/**
 * InlineTradePanel — qty stepper + live total + BUY/SELL that call the API
 * directly (with the same auto-retry logic as TradingSheet).
 */
export default function InlineTradePanel({ handle, currentPrice, multiplier, bitsMinted, isSqrt, balance, myHolding, minBitsToJoin, maxPerUser, isOwnClan, launchAtIso, onSuccess, onError, onReconciling }) {
  const { t } = useTranslation();
  const [qty, setQty] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [pendingSide, setPendingSide] = useState(null);
  // Batch-window countdown — ticks from 500ms down while the user's trade
  // is queued & shuffled by the server-side batch auction. Purely cosmetic
  // (server-authoritative for the actual window), but conveys the fairness
  // model. Matches the TradingSheet Commit & Settle UX.
  const [commitMs, setCommitMs] = useState(500);
  const [commitInfo, setCommitInfo] = useState(null);
  // Live queue-depth poll while committing — powers the "N others locked in
  // with you · est Xs" hint on the Commit strip. Only the latest snapshot
  // is kept; polls every 500ms while `submitting` is true.
  const [queueSnap, setQueueSnap] = useState(null);
  // Max slippage tolerance the trader will accept before their order rejects.
  // Unified with TradingSheet chips: Auto / +5% / +10%. `Auto` sends NO
  // max_total/min_total to the server — the batch engine's per-Bit math
  // always matches the frontend quote for a solo trader, so Auto never
  // bounces you out of your own trade. Tighter tiers are guardrails
  // against big concurrent price moves in hyped launches, at the cost of
  // occasional rejection. We DON'T offer +2%: with our frontend's
  // integral approximation for qty>32 (rounding drift ~0.5% vs backend's
  // per-Bit sum), a +2% floor can bounce a solo trader on large sells.
  // Persisted per user in localStorage so their choice carries across
  // both this panel and the Trade modal.
  const SLIPPAGE_OPTS = [
    { key: 'auto', label: 'Auto',  pct: null },
    { key: 'p5',   label: '+5%',   pct: 5 },
    { key: 'p10',  label: '+10%',  pct: 10 },
  ];
  const [slippageKey, setSlippageKey] = useState(() => {
    try {
      const raw = localStorage.getItem('clanster.slippage') || 'auto';
      // Migrate legacy `p2` (dropped as unsafe) → auto so nobody is stuck
      // with a tier we no longer render.
      return raw === 'p2' ? 'auto' : raw;
    } catch { return 'auto'; }
  });
  const setSlippagePersist = (k) => {
    setSlippageKey(k);
    try { localStorage.setItem('clanster.slippage', k); } catch {}
  };
  const slippageOpt = SLIPPAGE_OPTS.find((o) => o.key === slippageKey) || SLIPPAGE_OPTS[0];
  // Viewport tracker — used to swap placeholders + preview format between
  // desktop (full descriptive labels) and mobile (compact "Custom" +
  // parenthesised bits) so the row fits on one line without truncation.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 900px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 900px)');
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  // Currently "armed" preset (side + key). 1st tap arms + previews. 2nd tap
  // on the same key fires. Tapping a different key transfers arm state.
  // Null when nothing is armed (initial + after a trade completes).
  const [armedKey, setArmedKey] = useState(null);
  // Auto-disarm after 5s of inactivity. Prevents accidental fires when a
  // user arms a chip, walks away, and returns minutes later — a quick
  // second tap should NOT execute a stale-priced trade. Timer resets any
  // time `armedKey` changes (rearm, disarm, or after a trade completes).
  useEffect(() => {
    if (!armedKey) return undefined;
    const timeoutId = setTimeout(() => setArmedKey(null), 5000);
    return () => clearTimeout(timeoutId);
  }, [armedKey]);
  // Custom preset values — the raw string in each input. Semantics depend on
  // the paired mode (customBuyMode / customSellMode) — see below.
  const [customBuyPts, setCustomBuyPts] = useState('');
  const [customSellBits, setCustomSellBits] = useState('');
  // Input-unit modes for the Custom Buy / Custom Sell rows. Traders think
  // in different mental models — one wants "spend N pts", the next wants
  // "own N Bits", another wants "cash out N pts". Each mode is persisted
  // to localStorage so a trader's preferred unit sticks across sessions.
  //   Buy modes:  'pts'  → type pts, preview bits
  //               'bits' → type bits, preview pts required
  //   Sell modes: 'pct'  → type %, preview pts + bits sold
  //               'pts'  → type pts wanted, preview % + bits sold
  const [customBuyMode, setCustomBuyMode] = useState(() => {
    try {
      const v = localStorage.getItem('clanster.customBuyMode');
      return v === 'bits' ? 'bits' : 'pts';
    } catch { return 'pts'; }
  });
  const [customSellMode, setCustomSellMode] = useState(() => {
    try {
      const v = localStorage.getItem('clanster.customSellMode');
      return v === 'pts' ? 'pts' : 'pct';
    } catch { return 'pct'; }
  });
  const flipCustomBuyMode = () => {
    const next = customBuyMode === 'pts' ? 'bits' : 'pts';
    setCustomBuyMode(next);
    try { localStorage.setItem('clanster.customBuyMode', next); } catch {}
    setCustomBuyPts('');
    setArmedKey(null);
  };
  const flipCustomSellMode = () => {
    const next = customSellMode === 'pct' ? 'pts' : 'pct';
    setCustomSellMode(next);
    try { localStorage.setItem('clanster.customSellMode', next); } catch {}
    setCustomSellBits('');
    setArmedKey(null);
  };
  // Slippage panel collapsed by default — tucked behind a compact ⚙ pill.
  const [slippageOpen, setSlippageOpen] = useState(false);
  // Sell-Min info popover — the ⓘ badge on the Min chip opens a small
  // tap-away tooltip explaining what "Sell Min" actually does (sells the
  // excess above the membership floor so the trader keeps their Clan slot).
  // Native HTML title tooltips don't show on mobile taps, so this is the
  // touch-friendly replacement.
  const [minInfoOpen, setMinInfoOpen] = useState(false);
  useEffect(() => {
    if (!minInfoOpen) return undefined;
    const onDown = (e) => {
      if (!e.target.closest('.tp-chip2-info')) setMinInfoOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMinInfoOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [minInfoOpen]);
  const holding = myHolding?.bits_owned || 0;
  const canSell = holding >= 1;

  // Pre-launch detection + 1-second countdown. Locks the panel identically
  // to the own-clan case (grey out chips, replace hint copy) so users
  // can't attempt a doomed buy that the server would reject.
  const [prelaunchMs, setPrelaunchMs] = useState(() => {
    if (!launchAtIso) return 0;
    const ms = new Date(launchAtIso).getTime() - Date.now();
    return ms > 0 ? ms : 0;
  });
  useEffect(() => {
    if (!launchAtIso) { setPrelaunchMs(0); return undefined; }
    const tick = () => {
      const ms = new Date(launchAtIso).getTime() - Date.now();
      setPrelaunchMs(ms > 0 ? ms : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [launchAtIso]);
  const isPrelaunch = prelaunchMs > 0;
  const prelaunchCountdown = (() => {
    if (!isPrelaunch) return '';
    const s = Math.floor(prelaunchMs / 1000);
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const mins = Math.floor((s % 3600) / 60);
    const secs = s % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (days > 0) return `${days}d ${pad(hours)}h ${pad(mins)}m ${pad(secs)}s`;
    if (hours > 0) return `${pad(hours)}h ${pad(mins)}m ${pad(secs)}s`;
    return `${pad(mins)}m ${pad(secs)}s`;
  })();
  const isLocked = isOwnClan || isPrelaunch;

  // Compute buy cost for an arbitrary qty. Returns the GROSS pts the user
  // will spend (silent-fee accounting) — walks the curve to get the subtotal
  // then applies the user's effective fee rate so every downstream preview
  // (approxTotal, maxAffordable, bitsForPts) is what actually leaves the wallet.
  const effectiveFeeRate = Number(myHolding?.effective_fee_rate || 0.11);
  const costOf = (k) => {
    if (!multiplier || k < 1) return 0;
    const sub = isSqrt
      ? sqrtBuySubtotal(multiplier, bitsMinted, k)
      : (multiplier * k * (2 * bitsMinted + k + 1)) / 2;
    return sub * (1 + effectiveFeeRate);
  };

  // Mirror of `costOf` for SELLs — walks the curve DOWNWARD from the current
  // supply and deducts fees (seller receives less than the raw subtotal).
  // This is what actually lands in the wallet on a sell, matching backend
  // `_sell_prices` + silent-fee accounting. Used by every sell preview
  // (approxTotal when a sell is armed, and the Custom Sell inline `~pts`).
  const proceedsOf = (k) => {
    if (!multiplier || k < 1) return 0;
    const sub = isSqrt
      ? sqrtSellSubtotal(multiplier, bitsMinted, k)
      : (multiplier * k * (2 * bitsMinted - k + 1)) / 2;
    return Math.max(0, sub * (1 - effectiveFeeRate));
  };

  // Quick approx total — server returns the true price on trade. Displayed
  // as a friendly preview so users can gauge what they're about to spend
  // (buy) or receive (sell). Side-aware: swaps between costOf and
  // proceedsOf based on which chip the trader has armed.
  const isSellArmedForPreview = typeof armedKey === 'string' && armedKey.startsWith('sell');
  const approxTotal = useMemo(
    () => (isSellArmedForPreview ? proceedsOf(qty) : costOf(qty)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [multiplier, qty, bitsMinted, isSqrt, effectiveFeeRate, isSellArmedForPreview],
  );

  // Max affordable buy qty given the current wallet balance. Walks the curve
  // upward and stops at the first qty whose cost exceeds balance. Also
  // capped by the live whale limit (`maxPerUser - holding`) so the Max
  // preset never suggests a qty the backend would reject.
  const maxAffordable = useMemo(() => {
    if (!multiplier || balance <= 0) return 0;
    let lo = 0;
    let hi = 1;
    // Exponential grow-out — safe upper bound so we don't loop forever
    while (costOf(hi) <= balance && hi < 100000) hi *= 2;
    // Binary search
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (costOf(mid) <= balance) lo = mid;
      else hi = mid - 1;
    }
    // Whale-cap clamp — for sqrt clans this is the DYNAMIC cap the backend
    // enforces (max(100, bits_minted × whale_pct)). Skip when maxPerUser
    // isn't provided (fallback to legacy balance-only behavior).
    if (typeof maxPerUser === 'number' && maxPerUser > 0) {
      const roomForBuyer = Math.max(0, maxPerUser - holding);
      lo = Math.min(lo, roomForBuyer);
    }
    return lo;
  }, [multiplier, bitsMinted, isSqrt, balance, maxPerUser, holding]);

  // Invert curve: given a pts budget, return the max whole-Bit qty
  // affordable at the current supply. Reused by every pts-preset click and
  // by the Custom pts input. Binary-search on `costOf` so it's fast even
  // for large budgets.
  const bitsForPts = useCallback((budget) => {
    if (!multiplier || budget <= 0) return 0;
    let lo = 0, hi = 1;
    while (costOf(hi) <= budget && hi < 250000) hi *= 2;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (costOf(mid) <= budget) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }, [multiplier, bitsMinted, isSqrt]);

  // Invert the SELL side: given a target pts payout (what the trader wants
  // to receive), return the smallest whole-Bit qty whose `proceedsOf` meets
  // or exceeds that target. Powers Custom Sell in "pts" mode.
  //   • Clamped to the trader's holding — you can't sell more than you own.
  //   • Returns 0 if no whole-Bit sell reaches the target (e.g., asking for
  //     more pts than the entire bag would net).
  const bitsForProceeds = useCallback((targetPts) => {
    if (!multiplier || targetPts <= 0 || holding < 1) return 0;
    // Sanity ceiling: selling the full bag can't exceed proceedsOf(holding).
    if (proceedsOf(holding) < targetPts) return 0;
    let lo = 1, hi = holding;
    while (lo < hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (proceedsOf(mid) >= targetPts) hi = mid;
      else lo = mid + 1;
    }
    return lo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiplier, bitsMinted, isSqrt, holding, effectiveFeeRate]);

  // Adaptive buy presets — auto-scale so the SMALLEST tier always buys at
  // least 1 Bit at the current price. Always returns EXACTLY 3 tiers so
  // the BUY row consistently renders 5 chips (3 pts + Max + Custom). If
  // the user can't afford a tier the chip renders disabled.
  const buyPtsPresets = useMemo(() => {
    const fallback = [10, 25, 50];
    if (!currentPrice || currentPrice <= 0) return fallback;
    const ladder = [1, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000];
    const startIdx = ladder.findIndex((v) => v >= currentPrice);
    const base = ladder.slice(Math.max(0, startIdx), Math.max(0, startIdx) + 3);
    return base.length === 3 ? base : (base.concat(ladder.slice(-3)).slice(0, 3));
  }, [currentPrice]);

  // "Stay in Clan" sell qty — sells DOWN TO min_bits_to_join so the seller
  // retains membership. If already at or below the floor, returns 0 (button
  // is then disabled).
  const stayInClanQty = Math.max(0, holding - (minBitsToJoin || 1));

  // Slippage estimate: how much the AVG unit fill price for this qty differs
  // from the current spot price. Inherent to the bonding curve depth.
  const slippagePct = useMemo(() => {
    if (!currentPrice || qty < 1 || approxTotal <= 0) return 0;
    const avgUnitCost = approxTotal / qty;
    return ((avgUnitCost - currentPrice) / currentPrice) * 100;
  }, [approxTotal, qty, currentPrice]);

  // Preset tap handler — 1st tap arms + previews, 2nd tap on same key fires.
  // Tapping a DIFFERENT key transfers arm state instead of firing.
  const armOrFire = (key, side, qtyForKey) => {
    if (submitting || !handle || isLocked) return;
    if (qtyForKey < 1) return;
    if (side === 'sell' && qtyForKey > holding) return;
    setQty(qtyForKey);
    if (armedKey === key) {
      submit(side, qtyForKey);
    } else {
      setArmedKey(key);
    }
  };

  const submit = async (side, overrideQty) => {
    if (submitting || !handle || isLocked) {
      if (isOwnClan) onError("You can't trade your own Clan.");
      else if (isPrelaunch) onError(`Trading opens in ${prelaunchCountdown}.`);
      return;
    }
    const submitQty = Number.isFinite(overrideQty) && overrideQty > 0 ? overrideQty : qty;
    if (submitQty < 1) return;
    if (side === 'sell' && !canSell) {
      onError('You have no Bits to sell.');
      return;
    }
    playTick(side);
    setSubmitting(true);
    setPendingSide(side);
    setCommitInfo(null);
    setQueueSnap(null);
    // Kick off the 500ms visual countdown. Server may actually run in a
    // fast-path (single-trader) or a slightly longer window — this is a
    // best-effort UX indicator, not a hard timing contract.
    setCommitMs(500);
    const startedAt = Date.now();
    const tickHandle = setInterval(() => {
      const remaining = Math.max(0, 500 - (Date.now() - startedAt));
      setCommitMs(remaining);
    }, 50);
    // Poll queue-depth every 500ms — cheap read, no auth. Populates the
    // "N others locked in · est Xs wait" line on the Commit strip.
    const pollQueue = async () => {
      try {
        const { data } = await api.get(`/trade/queue-depth/${handle}`);
        setQueueSnap({ queued: data.queued, est_wait_s: data.est_wait_s });
      } catch (_) { /* silent */ }
    };
    pollQueue();
    const queuePollHandle = setInterval(pollQueue, 500);
    let currentQty = submitQty;
    let attempt = 0;
    const MAX = 4;
    let lastErr = null;
    // Idempotency key — generated ONCE per user tap and reused across the
    // retry loop. Server-side dedup (`trade_dedup` collection) guarantees
    // that if the same ID reaches the server twice (because a retry fired
    // while the first attempt was in-flight), the second call returns the
    // cached result of the first instead of executing a second trade. This
    // is what makes "response lost mid-flight, user retries" safe — the
    // user's balance can never double-debit.
    const clientTradeId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `ct-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    while (attempt < MAX) {
      try {
        // Compute the fill ceiling (buy) / floor (sell) from the current
        // approx cost × tolerance. Server compares this to actual fill and
        // auto-cancels with a 409 if the batch would breach it — we then
        // fall back to the same auto-retry loop we already use for 409s.
        // Auto (slippageOpt.pct === null) sends NO bound so the server
        // accepts any fair fill from the batch shuffle.
        const approxNow = costOf(currentQty);
        const body = {
          handle,
          qty: currentQty,
          client_trade_id: clientTradeId,
        };
        if (slippageOpt.pct != null) {
          const tol = slippageOpt.pct / 100;
          if (side === 'buy') {
            body.max_total = Number((approxNow * (1 + tol)).toFixed(4));
          } else {
            body.min_total = Number((approxNow * (1 - tol)).toFixed(4));
          }
        }
        const { data } = await api.post(side === 'buy' ? '/trade/buy' : '/trade/sell', body);
        clearInterval(tickHandle);
        clearInterval(queuePollHandle);
        setCommitMs(0);
        setQueueSnap(null);
        // Capture batch metadata so the transparency chip can show below.
        if (data.batch_size && data.batch_size > 1) {
          setCommitInfo({ batch_size: data.batch_size, batch_position: data.batch_position });
        }
        const shrunk = currentQty < qty;
        // Profit detection — SERVER's realized_pnl is authoritative (present
        // on every sell response since Feb 2026). Falls back to client-side
        // math only when the server didn't send it (very old backends).
        // Fixes the "confetti never fires on Iris" bug where users whose
        // cost_basis wasn't backfilled had client-side gross_paid = 0.
        let profitPts = 0;
        let profitPct = 0;
        if (side === 'sell') {
          if (typeof data.realized_pnl === 'number' && Number.isFinite(data.realized_pnl)) {
            profitPts = data.realized_pnl;
            const invested = data.total - data.realized_pnl;
            profitPct = invested > 0 ? (data.realized_pnl / invested) * 100 : 0;
          } else {
            // Legacy fallback (older backend, no realized_pnl in response).
            const avgFillPerBit = data.total / Math.max(1, currentQty);
            const grossPaid = Number(myHolding?.gross_paid || 0);
            const avgCostPerBit = holding > 0 ? (grossPaid / holding) : 0;
            const perBitProfit = avgCostPerBit > 0 ? (avgFillPerBit - avgCostPerBit) : 0;
            profitPts = perBitProfit * currentQty;
            profitPct = avgCostPerBit > 0 ? (perBitProfit / avgCostPerBit) * 100 : 0;
          }
        }
        // ---- Success handoff ----
        // Wrap onSuccess in try/catch so a bug in the parent's celebration
        // code (confetti library, share modal state, holdings refetch) can
        // never bubble back into THIS submit's outer catch — which would
        // then treat the trade as a failure and fire onError AFTER the
        // success flash already showed. Root cause of the Iris "success
        // then Trade Failed" flash-flip bug (Feb 2026).
        try {
          onSuccess({
            side,
            qty: currentQty,
            total: data.total,
            balance_after: data.balance_after,
            price_after: data.price_after,
            isProfitSell: side === 'sell' && profitPts > 0.01,
            profitPts,
            profitPct,
            invested: side === 'sell' ? Math.max(0, data.total - profitPts) : 0,
            msg: side === 'buy'
              ? (shrunk
                  ? `✓ Bought ${currentQty} Bit${currentQty > 1 ? 's' : ''} for ${data.total.toFixed(2)} pts (scaled down from ${qty} — price moved).`
                  : `✓ Bought ${currentQty} Bit${currentQty > 1 ? 's' : ''} for ${data.total.toFixed(2)} pts.`)
              : `✓ Sold ${currentQty} Bit${currentQty > 1 ? 's' : ''} — received ${data.total.toFixed(2)} pts.`,
          });
        } catch (successCbErr) {
          // Log so we can catch it in the browser console — but do NOT
          // re-raise. The trade already committed on the server.
          // eslint-disable-next-line no-console
          console.error('onSuccess handler threw — trade already committed:', successCbErr);
        }
        setSubmitting(false);
        setPendingSide(null);
        setArmedKey(null);
        setCustomBuyPts('');
        setCustomSellBits('');
        return;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        const detail = String(err?.response?.data?.detail || '').toLowerCase();
        const is409 = status === 409 && (detail.includes('price moved') || detail.includes('batch was busy') || detail.includes('past your ceiling'));
        const is400 = status === 400 && side === 'buy' && (detail.includes('insufficient balance') || detail.includes('not enough points'));
        if (!is409 && !is400) break;
        if (attempt >= MAX - 1) break;
        if (is400) {
          const next = Math.max(1, Math.floor(currentQty / 2));
          if (next === currentQty) break;
          currentQty = next;
        }
        attempt += 1;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 250 * attempt));
      }
    }
    clearInterval(tickHandle);
    clearInterval(queuePollHandle);
    setCommitMs(0);
    setQueueSnap(null);
    // ---- Reconciliation on lost / dropped response ---------------------
    // If the last error had NO HTTP response (network drop, proxy timeout,
    // connection reset), the trade MAY have executed server-side and the
    // 200 OK was just lost in transit. Before we scream "Trade failed" —
    // which would tempt the user to click Sell again and cause a real
    // double-execution — we poll GET /trade/status/{clientTradeId} up to
    // 3 times (500ms apart). If the server confirms the trade landed, we
    // treat it as success. This is the fix for the Iris-sell bug (Feb 2026)
    // where the user's Bits reduced despite seeing "Trade failed".
    const hadNoResponse = !!lastErr && !lastErr.response;
    if (hadNoResponse) {
      // Surface a "Confirming your trade..." chip while we double-check.
      // Parent handles it via the flash pill (tone: 'info'). Prevents the
      // user from staring at nothing for ~1.5s wondering if it worked.
      if (typeof onReconciling === 'function') onReconciling(true);
      for (let poll = 0; poll < 3; poll += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 500));
        try {
          // eslint-disable-next-line no-await-in-loop
          const { data: statusData } = await api.get(`/trade/status/${clientTradeId}`);
          if (statusData && statusData.found && statusData.result) {
            const data = statusData.result;
            if (data.batch_size && data.batch_size > 1) {
              setCommitInfo({ batch_size: data.batch_size, batch_position: data.batch_position });
            }
            // Same server-authoritative realized_pnl path as the success
            // block above — see comment there for details.
            let profitPts = 0;
            let profitPct = 0;
            if (side === 'sell') {
              if (typeof data.realized_pnl === 'number' && Number.isFinite(data.realized_pnl)) {
                profitPts = data.realized_pnl;
                const invested = data.total - data.realized_pnl;
                profitPct = invested > 0 ? (data.realized_pnl / invested) * 100 : 0;
              } else {
                const avgFillPerBit = data.total / Math.max(1, currentQty);
                const grossPaid = Number(myHolding?.gross_paid || 0);
                const avgCostPerBit = holding > 0 ? (grossPaid / holding) : 0;
                const perBitProfit = avgCostPerBit > 0 ? (avgFillPerBit - avgCostPerBit) : 0;
                profitPts = perBitProfit * currentQty;
                profitPct = avgCostPerBit > 0 ? (perBitProfit / avgCostPerBit) * 100 : 0;
              }
            }
            try {
              onSuccess({
                side,
                qty: currentQty,
                total: data.total,
                balance_after: data.balance_after,
                price_after: data.price_after,
                isProfitSell: side === 'sell' && profitPts > 0.01,
                profitPts,
                profitPct,
                invested: side === 'sell' ? Math.max(0, data.total - profitPts) : 0,
                msg: side === 'buy'
                  ? `✓ Bought ${currentQty} Bit${currentQty > 1 ? 's' : ''} for ${data.total.toFixed(2)} pts.`
                  : `✓ Sold ${currentQty} Bit${currentQty > 1 ? 's' : ''} — received ${data.total.toFixed(2)} pts.`,
              });
            } catch (successCbErr) {
              // eslint-disable-next-line no-console
              console.error('onSuccess handler threw during reconciliation:', successCbErr);
            }
            setSubmitting(false);
            setPendingSide(null);
            setArmedKey(null);
            setCustomBuyPts('');
            setCustomSellBits('');
            if (typeof onReconciling === 'function') onReconciling(false);
            return;
          }
        } catch (_) {
          // Status endpoint itself failed — keep polling.
        }
      }
      // Reconciliation exhausted — clear the chip so the error toast can
      // take its slot.
      if (typeof onReconciling === 'function') onReconciling(false);
    }
    const msg = lastErr?.response?.data?.detail || 'Trade failed. Try again.';
    onError(typeof msg === 'string' ? msg : 'Trade failed. Try again.');
    setSubmitting(false);
    setPendingSide(null);
  };

  return (
    <div className={`tp-trade${isLocked ? ' tp-trade-locked' : ''}`} data-testid="tp-trade-panel">
      {isOwnClan && (
        <div className="tp-trade-locked-hint" data-testid="tp-trade-locked-hint">
          <span className="tp-trade-locked-icon" aria-hidden="true">🔒</span>
          <span>{t('trade.panelOwnClanLock')}</span>
        </div>
      )}

      {/* ---- Presets (Feb 2026 rebuild) ------------------------------
          Two rows of 5 chips. Each chip label carries the side ("Buy" /
          "Sell") on top so the row reads uniformly. 1st tap on a chip
          arms it (previewed in the meta row below); 2nd tap fires.
      ------------------------------------------------------------- */}
      {!submitting && (
      <div className="tp-presets tp-presets-v2" data-testid="tp-presets">
        <div className="tp-preset-row buy-row">
          {(() => {
            const buys = [
              ...buyPtsPresets.slice(0, 3).map((p) => ({
                key: `buy-pts-${p}`,
                testid: `tp-preset-buy-${p}`,
                pts: p,
                qty: bitsForPts(p),
                label: `${p} pts`,
              })),
              {
                key: 'buy-max',
                testid: 'tp-preset-buy-max',
                pts: costOf(maxAffordable),
                qty: maxAffordable,
                label: 'Max',
              },
            ];
            return buys.map((b) => {
              const armed = armedKey === b.key;
              const disabled = b.qty < 1 || isLocked || submitting;
              return (
                <button
                  key={b.key}
                  type="button"
                  className={`tp-chip2 buy${armed ? ' armed' : ''}${disabled ? ' is-disabled' : ''}`}
                  onClick={() => armOrFire(b.key, 'buy', b.qty)}
                  disabled={disabled}
                  data-testid={b.testid}
                  aria-pressed={armed}
                  title={
                    b.qty >= 1
                      ? `${b.label}${b.key === 'buy-max' ? ` (${b.pts.toFixed(2)} pts)` : ''} — ${armed ? 'tap AGAIN to buy' : 'tap to arm'} ${b.qty} Bit${b.qty === 1 ? '' : 's'}`
                      : 'Not enough for a single Bit at current price'
                  }
                >
                  <span className="tp-chip2-side">Buy</span>
                  <span className="tp-chip2-main">{b.label}</span>
                  <span className="tp-chip2-sub">
                    {b.qty >= 1 ? `≈ ${b.qty} Bit${b.qty === 1 ? '' : 's'}` : '—'}
                  </span>
                  {armed && <span className="tp-chip2-armed">{t('trade.panelTapAgain')}</span>}
                </button>
              );
            });
          })()}
          {/* Custom BUY — dual-mode input. User can type EITHER pts to
              spend OR Bits to acquire; the other side auto-previews. Mode
              is toggled via the small pill next to the input and persisted
              per-user in localStorage. Arm-and-fire pattern preserved so
              two taps are still needed regardless of which unit was typed.
              Enter key fires when armed. */}
          {(() => {
            const key = 'buy-custom';
            const armed = armedKey === key;
            const raw = customBuyPts;
            const parsed = raw === '' ? 0 : Math.max(0, parseFloat(raw) || 0);
            // Compute qtyForCustom (Bits) from raw input depending on mode.
            //   • pts  mode: user typed pts → invert curve to bits
            //   • bits mode: user typed bits directly (whole number)
            const qtyForCustom = customBuyMode === 'pts'
              ? (parsed > 0 ? bitsForPts(parsed) : 0)
              : Math.max(0, Math.floor(parsed));
            const disabled = isLocked || submitting;
            const minPtsFor1Bit = costOf(1);
            const belowMin = customBuyMode === 'pts' && parsed > 0 && qtyForCustom < 1;
            // Preview text — what the OTHER side (not the input) looks like.
            const ptsRequired = qtyForCustom >= 1 ? costOf(qtyForCustom) : 0;
            const previewText = (() => {
              if (customBuyMode === 'pts') {
                if (belowMin) return `min ${minPtsFor1Bit.toFixed(minPtsFor1Bit >= 10 ? 0 : 2)} pts`;
                return qtyForCustom >= 1 ? `~${qtyForCustom} Bit${qtyForCustom === 1 ? '' : 's'}` : '';
              }
              // bits mode
              return qtyForCustom >= 1 ? `~${ptsRequired.toFixed(ptsRequired >= 100 ? 0 : 2)} pts` : '';
            })();
            const placeholder = isMobile
              ? 'Custom'
              : (customBuyMode === 'pts'
                  ? 'Enter custom pts to buy'
                  : 'Enter custom Bits to buy');
            const inputMode = customBuyMode === 'pts' ? 'decimal' : 'numeric';
            const unitLabel = customBuyMode === 'pts' ? 'pts' : 'Bits';
            return (
              <div
                className={`tp-chip2 buy custom tp-chip2-cbig${armed ? ' armed' : ''}${disabled ? ' is-disabled' : ''}${belowMin ? ' below-min' : ''}`}
                data-testid="tp-preset-buy-custom"
              >
                <span className="tp-chip2-cinput-wrap">
                  <input
                    type="text"
                    inputMode={inputMode}
                    className="tp-chip2-cinput"
                    value={raw}
                    disabled={disabled}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    onFocus={() => setArmedKey(null)}
                    onChange={(e) => {
                      // Sanitize by mode. Pts allows one decimal; Bits is
                      // whole-integer-only.
                      let clean;
                      if (customBuyMode === 'pts') {
                        clean = e.target.value.replace(/[^0-9.]/g, '');
                        // one decimal max
                        const parts = clean.split('.');
                        if (parts.length > 2) clean = `${parts[0]}.${parts.slice(1).join('')}`;
                      } else {
                        clean = e.target.value.replace(/[^0-9]/g, '');
                      }
                      setCustomBuyPts(clean);
                      const p = parseFloat(clean);
                      const bits = customBuyMode === 'pts'
                        ? (Number.isFinite(p) && p > 0 ? bitsForPts(p) : 0)
                        : (Number.isFinite(p) && p >= 1 ? Math.floor(p) : 0);
                      if (bits >= 1) setQty(bits); else setArmedKey(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && qtyForCustom >= 1 && !disabled) {
                        submit('buy', qtyForCustom);
                      }
                    }}
                    data-testid="tp-preset-buy-custom-input"
                  />
                </span>
                <button
                  type="button"
                  className="tp-chip2-cmode-pill"
                  onClick={(e) => { e.stopPropagation(); flipCustomBuyMode(); }}
                  aria-label={`Switch input to ${customBuyMode === 'pts' ? 'Bits' : 'pts'}`}
                  title={`Switch to ${customBuyMode === 'pts' ? 'Bits' : 'pts'} mode`}
                  data-testid="tp-preset-buy-custom-mode"
                  disabled={disabled}
                >
                  {unitLabel} <span className="tp-chip2-cmode-arrow" aria-hidden="true">⇄</span>
                </button>
                <span className="tp-chip2-cpreview" data-testid="tp-preset-buy-custom-preview">
                  {previewText}
                </span>
                <button
                  type="button"
                  className={`tp-chip2-cfire buy${armed ? ' armed' : ''}`}
                  disabled={disabled || qtyForCustom < 1}
                  data-testid="tp-preset-buy-custom-fire"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (disabled || qtyForCustom < 1) return;
                    if (armed) {
                      submit('buy', qtyForCustom);
                    } else {
                      setQty(qtyForCustom);
                      setArmedKey(key);
                    }
                  }}
                >
                  {armed ? t('trade.panelTapAgain') : 'Buy'}
                </button>
              </div>
            );
          })()}
        </div>

        <div className="tp-preset-row sell-row">
          {(() => {
            const sells = [
              { key: 'sell-25',  testid: 'tp-preset-sell-25',  label: '25%',  qty: Math.max(1, Math.floor(holding * 0.25)) },
              { key: 'sell-50',  testid: 'tp-preset-sell-50',  label: '50%',  qty: Math.max(1, Math.floor(holding * 0.5)) },
              { key: 'sell-max', testid: 'tp-preset-sell-max', label: 'Max',  qty: holding },
              { key: 'sell-min', testid: 'tp-preset-sell-min', label: 'Min',  qty: stayInClanQty },
            ];
            return sells.map((s) => {
              const armed = armedKey === s.key;
              const disabled = !canSell || s.qty < 1 || s.qty > holding || isLocked || submitting;
              const tip = s.key === 'sell-min'
                ? (s.qty >= 1
                    ? `Sells ${s.qty} — keeps the ${minBitsToJoin || 1}-Bit membership floor so you stay in the Clan`
                    : `You're at the ${minBitsToJoin || 1}-Bit floor — nothing to sell above it`)
                : (canSell
                    ? `${s.label} of your ${holding} Bit${holding === 1 ? '' : 's'} = ${s.qty} · ${armed ? 'tap AGAIN to sell' : 'tap to arm'}`
                    : 'You have no Bits to sell');
              return (
                <button
                  key={s.key}
                  type="button"
                  className={`tp-chip2 sell${armed ? ' armed' : ''}${disabled ? ' is-disabled' : ''}`}
                  onClick={() => armOrFire(s.key, 'sell', s.qty)}
                  disabled={disabled}
                  data-testid={s.testid}
                  aria-pressed={armed}
                  title={tip}
                >
                  {/* Small ⓘ hint on Sell Min — the "Min" label alone doesn't
                      convey that this preset SELLS the excess ABOVE the
                      membership floor so the trader stays in the Clan.
                      Tapping the badge opens an inline popover with a
                      plain-English explanation (mobile-friendly because
                      the HTML `title` attribute doesn't fire on tap). */}
                  {s.key === 'sell-min' && (
                    <span
                      role="button"
                      tabIndex={0}
                      className="tp-chip2-info"
                      title="What does Sell Min do?"
                      data-testid="tp-sell-min-info"
                      onClick={(e) => { e.stopPropagation(); setMinInfoOpen((v) => !v); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation(); e.preventDefault();
                          setMinInfoOpen((v) => !v);
                        }
                      }}
                    >
                      i
                      {minInfoOpen && (
                        <span
                          className="tp-chip2-info-pop"
                          role="tooltip"
                          data-testid="tp-sell-min-info-pop"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <b>{t('trade.sellMinInfoTitle')}</b>
                          {t('trade.sellMinInfoBody', { count: minBitsToJoin || 1 })}
                        </span>
                      )}
                    </span>
                  )}
                  <span className="tp-chip2-side">Sell</span>
                  <span className="tp-chip2-main">{s.label}</span>
                  <span className="tp-chip2-sub">
                    {/* Uniform "X Bit(s)" subtitle across all sell chips.
                        Sell Min used to show localized "at floor" / "keep
                        floor · X" — redundant with the ⓘ badge which spells
                        out the semantics on demand. Consistency > cleverness. */}
                    {s.qty >= 1 ? `${s.qty} Bit${s.qty === 1 ? '' : 's'}` : '—'}
                  </span>
                  {armed && <span className="tp-chip2-armed">{t('trade.panelTapAgain')}</span>}
                </button>
              );
            });
          })()}
          {(() => {
            const key = 'sell-custom';
            const armed = armedKey === key;
            const raw = customSellBits;
            // Compute qty (Bits) from raw input depending on mode.
            //   • pct mode: user typed 0-100 → floor(pct/100 × holding)
            //   • pts mode: user typed target pts → invert proceedsOf to bits
            let qtyForCustom = 0;
            let previewText = '';
            const disabled = !canSell || isLocked || submitting;
            if (customSellMode === 'pct') {
              const parsedPct = raw === '' ? 0 : Math.max(0, Math.min(100, parseFloat(raw) || 0));
              qtyForCustom = parsedPct > 0 ? Math.max(0, Math.floor((parsedPct / 100) * holding)) : 0;
              const previewPts = proceedsOf(qtyForCustom);
              const belowOnePct = parsedPct > 0 && qtyForCustom < 1;
              // Compact mobile preview drops the "Bits" word entirely — the
              // parenthesised number reads unambiguously as Bit count given
              // the row's context. Saves ~30px, keeps everything visible.
              const ptsStr = previewPts.toFixed(previewPts >= 100 ? 0 : 2);
              const bitsLbl = `${qtyForCustom} Bit${qtyForCustom === 1 ? '' : 's'}`;
              previewText = belowOnePct
                ? 'less than 1 Bit'
                : qtyForCustom >= 1
                  ? (isMobile
                      ? `~${ptsStr}pts (${qtyForCustom})`
                      : `~${ptsStr} pts · ${bitsLbl}`)
                  : '';
            } else {
              // pts mode — target pts to receive
              const parsedPts = raw === '' ? 0 : Math.max(0, parseFloat(raw) || 0);
              qtyForCustom = parsedPts > 0 ? bitsForProceeds(parsedPts) : 0;
              const actualPts = proceedsOf(qtyForCustom);
              const pctOfBag = holding > 0 && qtyForCustom > 0 ? (qtyForCustom / holding) * 100 : 0;
              // "unreachable" — user asked for more pts than the whole bag can yield.
              const unreachable = parsedPts > 0 && qtyForCustom === 0;
              const maxPossiblePts = proceedsOf(holding);
              const pctStr = pctOfBag.toFixed(pctOfBag >= 10 ? 0 : 1);
              const bitsLbl = `${qtyForCustom} Bit${qtyForCustom === 1 ? '' : 's'}`;
              const ptsStr = actualPts.toFixed(actualPts >= 100 ? 0 : 2);
              previewText = unreachable
                ? (holding >= 1
                    ? `max ~${maxPossiblePts.toFixed(maxPossiblePts >= 100 ? 0 : 2)} pts`
                    : 'no Bits to sell')
                : qtyForCustom >= 1
                  ? (isMobile
                      ? `~${pctStr}% (${qtyForCustom})`
                      : `~${pctStr}% · ${bitsLbl} · ~${ptsStr} pts`)
                  : '';
            }
            const overBag = qtyForCustom > holding;
            const belowOne = customSellMode === 'pct'
              && raw !== ''
              && parseFloat(raw) > 0
              && qtyForCustom < 1;
            const placeholder = isMobile
              ? 'Custom'
              : (customSellMode === 'pct'
                  ? 'Enter custom % to sell'
                  : 'Enter pts you want to receive');
            const inputMode = customSellMode === 'pct' ? 'numeric' : 'decimal';
            const unitLabel = customSellMode === 'pct' ? '%' : 'pts';
            return (
              <div
                className={`tp-chip2 sell custom tp-chip2-cbig${armed ? ' armed' : ''}${disabled ? ' is-disabled' : ''}${belowOne ? ' below-min' : ''}`}
                data-testid="tp-preset-sell-custom"
              >
                <span className="tp-chip2-cinput-wrap" data-testid="tp-preset-sell-custom-wrap">
                  <input
                    type="text"
                    inputMode={inputMode}
                    className="tp-chip2-cinput"
                    value={raw}
                    disabled={disabled}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    onFocus={() => setArmedKey(null)}
                    onChange={(e) => {
                      let clean;
                      if (customSellMode === 'pct') {
                        // Whole integer, clamp to [0..100], drop any extras.
                        clean = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                        if (clean !== '' && parseInt(clean, 10) > 100) clean = '100';
                      } else {
                        // Pts — one decimal max
                        clean = e.target.value.replace(/[^0-9.]/g, '');
                        const parts = clean.split('.');
                        if (parts.length > 2) clean = `${parts[0]}.${parts.slice(1).join('')}`;
                      }
                      setCustomSellBits(clean);
                      setArmedKey(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && qtyForCustom >= 1 && !disabled) {
                        submit('sell', qtyForCustom);
                      }
                    }}
                    data-testid="tp-preset-sell-custom-input"
                  />
                </span>
                <button
                  type="button"
                  className="tp-chip2-cmode-pill sell"
                  onClick={(e) => { e.stopPropagation(); flipCustomSellMode(); }}
                  aria-label={`Switch input to ${customSellMode === 'pct' ? 'pts' : 'percent'}`}
                  title={`Switch to ${customSellMode === 'pct' ? 'pts' : '%'} mode`}
                  data-testid="tp-preset-sell-custom-mode"
                  disabled={disabled}
                >
                  {unitLabel} <span className="tp-chip2-cmode-arrow" aria-hidden="true">⇄</span>
                </button>
                <span className="tp-chip2-cpreview" data-testid="tp-preset-sell-custom-preview">
                  {previewText}
                </span>
                <button
                  type="button"
                  className={`tp-chip2-cfire sell${armed ? ' armed' : ''}`}
                  disabled={disabled || qtyForCustom < 1 || overBag}
                  data-testid="tp-preset-sell-custom-fire"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (disabled || qtyForCustom < 1 || overBag) return;
                    if (armed) {
                      submit('sell', qtyForCustom);
                    } else {
                      setQty(qtyForCustom);
                      setArmedKey(key);
                    }
                  }}
                >
                  {armed ? t('trade.panelTapAgain') : 'Sell'}
                </button>
              </div>
            );
          })()}
        </div>
      </div>
      )}

      {/* Preview meta — one row: 2 distinct field blocks (You Spend/Earn
          + Avg vs Spot) followed by an inline ⚙ Slippage pill. Slippage
          chips reveal below on tap. Label swaps to "You earn" when the
          armed side is a SELL — matches the direction of Points flow. */}
      {!submitting && (() => {
        const isSellArmed = typeof armedKey === 'string' && armedKey.startsWith('sell');
        return (
        <div className="tp-meta-row" data-testid="tp-meta-row">
          <div className="tp-meta-block" data-testid="tp-meta-spend"
            title={isSellArmed
              ? "Estimated pts landing in your wallet. Fees baked in."
              : "Estimated pts leaving your wallet. Includes the 11% round-trip fee split silently."}>
            <div className="tp-meta-lbl">{isSellArmed ? t('trade.panelYouEarn') : t('trade.panelYouSpend')}</div>
            <div className="tp-meta-val">
              {armedKey ? `${approxTotal.toFixed(2)} pts` : '—'}
            </div>
          </div>
          <div className="tp-meta-block" data-testid="tp-meta-avg"
            title="Average unit price of your order vs the current spot price. Bigger orders walk further up (buy) or down (sell) the curve, so the avg drifts from spot — that gap is the curve's natural depth impact, not a hidden fee.">
            <div className="tp-meta-lbl">{t('trade.panelAvgVsSpot')}</div>
            <div className={`tp-meta-val ${Math.abs(slippagePct) > 5 ? 'warn' : ''}`}>
              {armedKey ? `${slippagePct >= 0 ? '+' : ''}${slippagePct.toFixed(2)}%` : '—'}
            </div>
          </div>
          <button
            type="button"
            className={`tp-slippage-pill${slippageOpen ? ' open' : ''}`}
            onClick={() => setSlippageOpen((v) => !v)}
            aria-expanded={slippageOpen}
            data-testid="tp-slippage-toggle"
            title={slippageOpt.pct == null
              ? 'Auto — the server accepts any fair fill from the batch shuffle. Recommended.'
              : 'If the batch shuffle would push your fill past this % above (buy) or below (sell) the current spot, the server auto-cancels your order.'}
          >
            <span aria-hidden="true">⚙</span>
            <span className="tp-slippage-pill-txt">Slippage <b>{slippageOpt.label}</b></span>
            <span className="tp-slippage-chev" aria-hidden="true">{slippageOpen ? '▴' : '▾'}</span>
          </button>
        </div>
        );
      })()}

      {/* Slippage chips — expand below the meta row when toggle is open.
          Unified with TradingSheet: Auto / +5% / +10%. +2% was intentionally
          dropped — it could bounce solo traders on large sells due to
          frontend integral vs backend per-Bit sum drift. */}
      {!submitting && slippageOpen && (
        <div className="tp-slippage-chips-row" role="radiogroup" aria-label="Max slippage tolerance" data-testid="tp-slippage-chips-row">
          {SLIPPAGE_OPTS.map((o) => (
            <button
              key={o.key}
              type="button"
              className={`tp-slippage-chip${slippageKey === o.key ? ' active' : ''}`}
              onClick={() => setSlippagePersist(o.key)}
              data-testid={`tp-slippage-${o.key}`}
            >{o.label}</button>
          ))}
          <div className="tp-slippage-hint" data-testid="tp-slippage-hint">
            {slippageOpt.pct == null
              ? 'Auto ✓ — always fills your trade at a fair price. Recommended.'
              : 'Tighter tiers only help on hyped launches — they can also bounce your own trade. Auto is safest.'}
          </div>
        </div>
      )}

      {/* ⚠️ Commit & Settle — takes over the whole panel while a trade is in flight. */}
      {submitting && (
        <div className="tp-commit" data-testid="tp-commit-strip">
          <div className="tp-commit-lock" aria-hidden="true">🔒</div>
          <div className="tp-commit-copy">
            <div className="tp-commit-head">
              Locked in ·&nbsp;
              <b>{pendingSide === 'sell' ? 'Sell' : 'Buy'} {qty} Bit{qty > 1 ? 's' : ''}</b>
            </div>
            <div className="tp-commit-sub" data-testid="tp-commit-countdown">
              {commitMs > 0
                ? `Batch fires in ${(commitMs / 1000).toFixed(1)}s · random shuffle keeps it fair`
                : 'Settling…'}
            </div>
            {queueSnap && queueSnap.queued >= 3 && (
              <div className="tp-commit-queue" data-testid="tp-commit-queue">
                🔥 <b>{queueSnap.queued}</b> other{queueSnap.queued === 1 ? '' : 's'} locked in with you ·&nbsp;
                est <b>{queueSnap.est_wait_s < 1
                  ? `${Math.round(queueSnap.est_wait_s * 1000)}ms`
                  : `${queueSnap.est_wait_s.toFixed(1)}s`}</b> wait · shuffled fair
              </div>
            )}
            <div className="tp-commit-bar">
              <div
                className="tp-commit-bar-fill"
                style={{ width: `${((500 - commitMs) / 500) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Double-tap safety note. */}
      {!submitting && (
        <div className="tp-no-confirm-hint" data-testid="tp-no-confirm-hint" aria-live="polite">
          <span aria-hidden="true">⚡</span>&nbsp;{t('trade.panelTapTwiceHint')}
        </div>
      )}

      {/* Batch transparency chip — only when your last trade was actually
          part of a >1 batch. Same phrasing as TradingSheet's chip. */}
      {commitInfo && !submitting && (
        <div className="tp-batch-chip" data-testid="tp-batch-chip">
          <span aria-hidden="true">🎲</span>
          &nbsp;Batch of <b>{commitInfo.batch_size}</b> · your trade landed at&nbsp;
          <b>#{commitInfo.batch_position}</b> (randomised)
        </div>
      )}
    </div>
  );
}
